import { fetchAnimalDeletionSummary } from '@/lib/animalDeletionSummary';
import { apiFetch, apiUpload, getToken } from '@/lib/api';
import type { AnimalMediaPageResponse } from '@/lib/mediaPreview';
import { animalToPatchBody } from '@/lib/pedigree';
import type {
  Animal,
  AnimalChangeLogEntry,
  AnimalMediaItem,
  AnimalTimelineResponse,
  AppNotification,
  Breed,
  Category,
  Client,
  Lot,
  PaginationMeta,
  Property,
  SyncConflict,
  User,
} from '@/types/api';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

export const queryKeys = {
  me: ['auth', 'me'] as const,
  client: ['client'] as const,
  properties: ['properties'] as const,
  property: (id: number) => ['properties', id] as const,
  lots: (propertyId: number) => ['lots', propertyId] as const,
  lotMembershipHistory: (lotId: number, filters: Record<string, unknown>) =>
    ['lots', 'membership-history', lotId, filters] as const,
  animals: (filters: Record<string, unknown>) => ['animals', filters] as const,
  animal: (id: number) => ['animals', 'detail', id] as const,
  breeds: ['catalog', 'breeds'] as const,
  categories: ['catalog', 'categories'] as const,
  events: (filters: Record<string, unknown>) => ['events', filters] as const,
  movements: (filters: Record<string, unknown>) => ['movements', filters] as const,
  reproduction: (filters: Record<string, unknown>) => ['reproduction', filters] as const,
  semen: ['semen'] as const,
  conflicts: (resolution?: string, page?: number) =>
    ['sync', 'conflicts', resolution, page] as const,
  pendingConflicts: ['sync', 'pending-count'] as const,
  animalMedia: (animalId: number) => ['animals', 'media', animalId] as const,
  animalTimeline: (animalId: number) => ['animals', 'timeline', animalId] as const,
  animalChanges: (animalId: number) => ['animals', 'changes', animalId] as const,
  animalDeletionSummary: (animalId: number) => ['animals', 'deletion-summary', animalId] as const,
  notifications: (filters?: Record<string, unknown>) => ['notifications', filters ?? {}] as const,
  notificationsUnread: ['notifications', 'unread-count'] as const,
  dashboardSummary: (propertyId: number | null) => ['dashboard', 'summary', propertyId] as const,
};

export function useClient() {
  return useQuery({
    queryKey: queryKeys.client,
    queryFn: () => apiFetch<{ client: Client }>('/client').then((d) => d.client),
    enabled: !!getToken(),
  });
}

export type DashboardAlert = {
  type: string;
  severity: 'info' | 'warning' | 'error';
  title: string;
  body: string;
  action_url: string;
  count: number;
};

export type DashboardSummary = {
  property_id: number | null;
  counts: {
    animals_active: number;
    animals_male: number;
    animals_female: number;
    events_total: number;
    pregnant: number;
    calving_next_7_days: number;
    calving_next_30_days: number;
    weighing_overdue: number;
    pending_conflicts: number;
    unread_notifications: number;
  };
  lots: Array<{
    id: number;
    name: string;
    code: string | null;
    lot_type: string | null;
    active_animals_count: number;
    property_id: number;
  }>;
  calving_soon: Array<{
    cow_id: number;
    cow_internal_number: string;
    cow_name: string | null;
    expected_calving_date: string;
    property_id: number;
    within_7_days: boolean;
  }>;
  recent_events: TimelineEvent[];
  recent_movements: MovementRow[];
  alerts: DashboardAlert[];
};

export function useDashboardSummary(propertyId: number | null) {
  const params = new URLSearchParams();
  if (propertyId) params.set('property_id', String(propertyId));
  const qs = params.toString();

  return useQuery({
    queryKey: queryKeys.dashboardSummary(propertyId),
    queryFn: () =>
      apiFetch<DashboardSummary>(`/dashboard/summary${qs ? `?${qs}` : ''}`),
    enabled: !!getToken() && !!propertyId,
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ client: Client }>('/client', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.client }),
  });
}

export function useUploadClientLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('logo', file);
      return apiUpload<{ client: Client }>('/client/logo', formData);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.client }),
  });
}

export function useRemoveClientLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ client: Client }>('/client/logo', { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.client }),
  });
}

export function useNotifications(filters: { unread_only?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (filters.unread_only) qs.set('unread_only', '1');
  const query = qs.toString();

  return useQuery({
    queryKey: queryKeys.notifications(filters),
    queryFn: () =>
      apiFetch<{
        notifications: AppNotification[];
        unread_count: number;
        meta: PaginationMeta;
      }>(`/notifications${query ? `?${query}` : ''}`),
    enabled: !!getToken(),
    refetchInterval: 60_000,
  });
}

export function useNotificationsUnreadCount() {
  return useQuery({
    queryKey: queryKeys.notificationsUnread,
    queryFn: () =>
      apiFetch<{ unread_count: number }>('/notifications/unread-count').then((d) => d.unread_count),
    enabled: !!getToken(),
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ unread_count: number }>(`/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ unread_count: number }>('/notifications/read-all', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useProperties() {
  return useQuery({
    queryKey: queryKeys.properties,
    queryFn: () =>
      apiFetch<{ properties: Property[]; meta: PaginationMeta }>('/properties?per_page=100'),
  });
}

export function useLots(propertyId: number | null) {
  return useQuery({
    queryKey: queryKeys.lots(propertyId ?? 0),
    queryFn: () =>
      apiFetch<{ lots: Lot[]; meta: PaginationMeta }>(
        `/properties/${propertyId}/lots?per_page=100`,
      ),
    enabled: !!propertyId,
  });
}

export type AnimalSortColumn =
  | 'internal_number'
  | 'animal_name'
  | 'sex'
  | 'category_name'
  | 'current_weight_kg'
  | 'birth_date'
  | 'lot_name';

export function useAnimals(filters: {
  property_id?: number;
  lot_id?: number;
  sex?: string;
  search?: string;
  sort?: AnimalSortColumn;
  sort_dir?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
  include_archive?: boolean;
  only_archive?: boolean;
}) {
  const params = new URLSearchParams();
  if (filters.property_id) params.set('property_id', String(filters.property_id));
  if (filters.lot_id) params.set('lot_id', String(filters.lot_id));
  if (filters.sex) params.set('sex', filters.sex);
  if (filters.search) params.set('search', filters.search);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.sort_dir) params.set('sort_dir', filters.sort_dir);
  if (filters.include_archive) params.set('include_archive', '1');
  if (filters.only_archive) params.set('only_archive', '1');
  params.set('per_page', String(filters.per_page ?? 50));
  if (filters.page) params.set('page', String(filters.page));

  return useQuery({
    queryKey: queryKeys.animals(filters),
    queryFn: () =>
      apiFetch<{ animals: AnimalSummary[]; meta: PaginationMeta }>(
        `/animals?${params}`,
      ),
    enabled: !!filters.property_id,
  });
}

export interface AnimalSummary {
  id: number;
  property_id: number;
  property_name: string;
  lot_id: number | null;
  lot_name: string | null;
  internal_number: string;
  animal_name: string | null;
  sex: 'male' | 'female';
  breed_abbreviation: string | null;
  breed_name: string | null;
  category_name: string;
  category_code: string;
  birth_date: string | null;
  current_weight_kg: number | null;
  last_weighing_date?: string | null;
  status: string;
  is_archive?: boolean;
  mother_id?: number | null;
  father_id?: number | null;
  cover_photo_url?: string | null;
  cover_media_id?: number | null;
}

export type LotMembershipHistoryItem = AnimalSummary & {
  entered_at: string | null;
  exited_at: string | null;
  exit_reason: 'transfer' | 'sale' | 'death' | 'off_property' | null;
};

export function useLotMembershipHistory(
  lotId: number | undefined,
  filters: { search?: string; sex?: string } = {},
) {
  return useQuery({
    queryKey: queryKeys.lotMembershipHistory(lotId ?? 0, filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.sex) params.set('sex', filters.sex);
      const qs = params.toString();
      return apiFetch<{ memberships: LotMembershipHistoryItem[]; meta: { total: number } }>(
        `/lots/${lotId}/membership-history${qs ? `?${qs}` : ''}`,
      );
    },
    enabled: !!lotId && lotId > 0,
  });
}

export function useAnimal(id: number) {
  return useQuery({
    queryKey: queryKeys.animal(id),
    queryFn: () => apiFetch<{ animal: Animal }>(`/animals/${id}`).then((d) => d.animal),
    enabled: id > 0,
  });
}

export function useAnimalMedia(animalId: number) {
  return useQuery({
    queryKey: queryKeys.animalMedia(animalId),
    queryFn: () =>
      apiFetch<{ media: AnimalMediaItem[] }>(`/animals/${animalId}/media`).then((d) => d.media),
    enabled: animalId > 0,
  });
}

const ANIMAL_MEDIA_PAGE_SIZE = 24;

export function useInfiniteAnimalMedia(animalId: number, perPage = ANIMAL_MEDIA_PAGE_SIZE) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.animalMedia(animalId), 'infinite', perPage] as const,
    queryFn: ({ pageParam }) =>
      apiFetch<AnimalMediaPageResponse>(
        `/animals/${animalId}/media?page=${pageParam}&per_page=${perPage}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.meta?.has_more ? (last.meta.page ?? 1) + 1 : undefined,
    enabled: animalId > 0,
  });
}

function invalidateAnimalVisuals(qc: ReturnType<typeof useQueryClient>, animalId: number) {
  qc.invalidateQueries({ queryKey: ['animals'] });
  qc.invalidateQueries({ queryKey: queryKeys.animal(animalId) });
  qc.invalidateQueries({ queryKey: queryKeys.animalMedia(animalId) });
  qc.invalidateQueries({ queryKey: queryKeys.animalTimeline(animalId) });
  qc.invalidateQueries({ queryKey: queryKeys.animalChanges(animalId) });
}

async function uploadAnimalMediaItem(
  animalId: number,
  file: File,
  options: {
    setAsCover?: boolean;
    thumbnail?: File;
    metadata?: Record<string, unknown>;
  } = {},
) {
  const formData = new FormData();
  formData.append('file', file);
  if (options.setAsCover) {
    formData.append('set_as_cover', 'true');
  }
  if (options.thumbnail) {
    formData.append('thumbnail', options.thumbnail);
  }
  formData.append(
    'metadata',
    JSON.stringify({
      captured_at: new Date().toISOString(),
      ...options.metadata,
    }),
  );

  const { media_item: item } = await apiUpload<{ media_item: AnimalMediaItem }>(
    `/animals/${animalId}/media`,
    formData,
  );

  if (options.setAsCover && item.media.media_kind === 'photo' && !item.is_primary) {
    await apiFetch<{ media_item: AnimalMediaItem }>(
      `/animals/${animalId}/media/${item.id}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ is_primary: true }),
      },
    );
  }

  return item;
}

export function useUploadAnimalMedia(animalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: File | { file: File; thumbnail?: File }) => {
      const file = payload instanceof File ? payload : payload.file;
      const thumbnail = payload instanceof File ? undefined : payload.thumbnail;
      return uploadAnimalMediaItem(animalId, file, {
        thumbnail,
        metadata: { source: 'web_gallery' },
      });
    },
    onSuccess: () => invalidateAnimalVisuals(qc, animalId),
  });
}

export function useUploadAnimalCover(animalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      originalFile,
    }: {
      file: File;
      /** Quando enviada do dispositivo, a original inteira vai para a galeria. */
      originalFile?: File;
    }) => {
      if (originalFile) {
        await uploadAnimalMediaItem(animalId, originalFile, {
          metadata: { source: 'web_upload', role: 'original' },
        });
      }

      return uploadAnimalMediaItem(animalId, file, {
        setAsCover: true,
        metadata: {
          source: 'web_cover',
          role: 'cover_crop',
          ...(originalFile ? { has_original_in_gallery: true } : { from_gallery: true }),
        },
      });
    },
    onSuccess: () => invalidateAnimalVisuals(qc, animalId),
  });
}

export function useSetAnimalCover(animalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: number) =>
      apiFetch<{ media_item: AnimalMediaItem }>(`/animals/${animalId}/media/${mediaId}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_primary: true }),
      }),
    onSuccess: () => invalidateAnimalVisuals(qc, animalId),
  });
}

export function useClearAnimalCover(animalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ message: string }>(`/animals/${animalId}/cover`, { method: 'DELETE' }),
    onSuccess: () => invalidateAnimalVisuals(qc, animalId),
  });
}

export function useDeleteAnimalMedia(animalId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: number) =>
      apiFetch<{ message: string }>(`/animals/${animalId}/media/${mediaId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateAnimalVisuals(qc, animalId),
  });
}

export function useAnimalTimeline(animalId: number) {
  return useQuery({
    queryKey: queryKeys.animalTimeline(animalId),
    queryFn: () => apiFetch<AnimalTimelineResponse>(`/animals/${animalId}/timeline`),
    enabled: animalId > 0,
  });
}

export function useAnimalChanges(animalId: number) {
  return useQuery({
    queryKey: queryKeys.animalChanges(animalId),
    queryFn: () =>
      apiFetch<{ changes: AnimalChangeLogEntry[] }>(`/animals/${animalId}/changes`).then(
        (d) => d.changes,
      ),
    enabled: animalId > 0,
  });
}

export interface AnimalDeletionRelatedItem {
  key: string;
  label: string;
  count: number;
}

export interface AnimalDeletionSummary {
  animal: {
    id: number;
    internal_number: string;
    name: string | null;
  };
  counts: {
    media: number;
    events: number;
    movements: number;
    offspring: number;
    total: number;
  };
  related: AnimalDeletionRelatedItem[];
  has_related_records: boolean;
}

export function useAnimalDeletionSummary(
  animal: AnimalSummary | null,
  propertyAnimals: AnimalSummary[] | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...queryKeys.animalDeletionSummary(animal?.id ?? 0), propertyAnimals?.length ?? 0],
    queryFn: () => fetchAnimalDeletionSummary(animal!, propertyAnimals),
    enabled: !!animal && enabled,
    retry: false,
  });
}

export function useDeleteAnimal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (animalId: number) =>
      apiFetch<{ message: string }>(`/animals/${animalId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['animals'] }),
  });
}

export function useBreeds() {
  return useQuery({
    queryKey: queryKeys.breeds,
    queryFn: () =>
      apiFetch<{ breeds: Breed[] }>('/catalog/breeds').then((d) => d.breeds),
    staleTime: 5 * 60_000,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories,
    queryFn: () =>
      apiFetch<{ categories: Category[] }>('/catalog/categories').then((d) => d.categories),
    staleTime: 5 * 60_000,
  });
}

export function useEvents(filters: { property_id?: number; event_type?: string; page?: number }) {
  const params = new URLSearchParams();
  if (filters.property_id) params.set('property_id', String(filters.property_id));
  if (filters.event_type) params.set('event_type', filters.event_type);
  params.set('per_page', '30');
  if (filters.page) params.set('page', String(filters.page));

  return useQuery({
    queryKey: queryKeys.events(filters),
    queryFn: () =>
      apiFetch<{ events: TimelineEvent[]; meta: PaginationMeta }>(`/events?${params}`),
    enabled: !!filters.property_id,
  });
}

export interface TimelineEvent {
  event_id: number;
  property_id: number;
  property_name: string;
  event_type: string;
  event_date: string;
  notes: string | null;
  responsible_name?: string | null;
  created_by_user_id?: number | null;
  created_by_name?: string | null;
  created_at?: string | null;
  subject_animal_id?: number | null;
  subject_internal_number?: string | null;
  subject_animal_name?: string | null;
  cow_animal_id?: number | null;
  cow_internal_number?: string | null;
  cow_animal_name?: string | null;
  bull_animal_id?: number | null;
  bull_internal_number?: string | null;
  bull_animal_name?: string | null;
  calf_animal_id?: number | null;
  calf_internal_number?: string | null;
  calf_animal_name?: string | null;
  weighing_weight_kg?: number | null;
  weaning_weight_kg?: number | null;
  milk_weight_kg?: number | null;
  milk_production_liters?: number | null;
  milk_milking_shift?: string | null;
  milk_milking_time?: string | null;
  is_drying_off?: boolean | null;
  drying_off_date?: string | null;
  is_pregnant?: boolean | null;
  expected_calving_date?: string | null;
  cover_event_id?: number | null;
  calf_sex?: 'male' | 'female' | null;
  natural_mount_cow_id?: number | null;
  insemination_cow_id?: number | null;
  medication_treatment_kind?: 'medication' | 'vaccination' | null;
  medication_product_name?: string | null;
  medication_dose?: string | null;
  medication_route?: string | null;
  medication_product_batch?: string | null;
  medication_next_dose_date?: string | null;
}

export function useMovements(filters: { property_id?: number; page?: number }) {
  const params = new URLSearchParams();
  if (filters.property_id) params.set('property_id', String(filters.property_id));
  params.set('per_page', '30');
  if (filters.page) params.set('page', String(filters.page));

  return useQuery({
    queryKey: queryKeys.movements(filters),
    queryFn: () =>
      apiFetch<{ movements: MovementRow[]; meta: PaginationMeta }>(`/movements?${params}`),
    enabled: !!filters.property_id,
  });
}

export interface MovementRow {
  id: number;
  property_id: number;
  movement_type: string;
  movement_date: string;
  scope: string;
  lot_id: number | null;
  notes: string | null;
}

export function useReproduction(filters: {
  property_id?: number;
  page?: number;
  per_page?: number;
}) {
  const params = new URLSearchParams();
  if (filters.property_id) params.set('property_id', String(filters.property_id));
  params.set('per_page', String(filters.per_page ?? 50));
  if (filters.page) params.set('page', String(filters.page));

  return useQuery({
    queryKey: queryKeys.reproduction(filters),
    queryFn: () =>
      apiFetch<{ reproduction: ReproductionRow[]; meta: PaginationMeta }>(
        `/reproduction/status?${params}`,
      ),
    enabled: !!filters.property_id,
  });
}

export interface ReproductionRow {
  cow_id: number;
  cow_internal_number: string;
  cow_name: string | null;
  bull_internal_number: string | null;
  diagnosis_date: string;
  cover_date: string | null;
  cover_type: string | null;
  is_pregnant: boolean;
  expected_calving_date: string | null;
}

export function usePendingConflicts() {
  return useQuery({
    queryKey: queryKeys.pendingConflicts,
    queryFn: () =>
      apiFetch<{ pending_count: number }>('/sync/conflicts/pending-count').then(
        (d) => d.pending_count,
      ),
    refetchInterval: 60_000,
  });
}

export function useSyncConflicts(resolution = 'pending', page = 1) {
  return useQuery({
    queryKey: queryKeys.conflicts(resolution, page),
    queryFn: () =>
      apiFetch<{ conflicts: SyncConflict[]; meta: PaginationMeta & { pending_count: number } }>(
        `/sync/conflicts?resolution=${resolution}&per_page=50&page=${page}`,
      ),
  });
}

export function useCreateProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ property: Property }>('/properties', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.properties }),
  });
}

export function useCreateAnimal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ animal: Animal }>('/animals', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['animals'] }),
  });
}

export function useUpdateAnimal(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ animal: Animal }>(`/animals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['animals'] });
      qc.invalidateQueries({ queryKey: queryKeys.animal(id) });
      qc.invalidateQueries({ queryKey: queryKeys.animalChanges(id) });
    },
  });
}

export function usePatchAnimalPedigree() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      animalId,
      patch,
    }: {
      animalId: number;
      patch: Partial<Pick<Animal, 'mother_id' | 'father_id'>>;
      refreshAnimalIds?: number[];
    }) => {
      const { animal } = await apiFetch<{ animal: Animal }>(`/animals/${animalId}`);
      const body = { ...animalToPatchBody(animal), ...patch };
      return apiFetch<{ animal: Animal }>(`/animals/${animalId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    onSuccess: (_, vars) => {
      const ids = new Set([vars.animalId, ...(vars.refreshAnimalIds ?? [])]);
      qc.invalidateQueries({ queryKey: ['animals'] });
      ids.forEach((id) => {
        qc.invalidateQueries({ queryKey: queryKeys.animal(id) });
        qc.invalidateQueries({ queryKey: queryKeys.animalChanges(id) });
      });
    },
  });
}

export function useCreateLot(propertyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ lot: Lot }>(`/properties/${propertyId}/lots`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.lots(propertyId) }),
  });
}

export function useDeleteLot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lotId: number) =>
      apiFetch<{ message: string }>(`/lots/${lotId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lots'] });
      qc.invalidateQueries({ queryKey: ['animals'] });
    },
  });
}

export function useRegisterEvent(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch(`/events/${type}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['reproduction'] });
      qc.invalidateQueries({ queryKey: ['animals'] });
    },
  });
}

export function useRegisterBatchEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      event_type: string;
      property_id: number;
      event_date: string;
      lot_id?: number | null;
      responsible_name?: string | null;
      notes?: string | null;
      bull_id?: number;
      is_pregnant?: boolean;
      treatment_kind?: string;
      product_name?: string;
      dose?: string;
      route?: string;
      product_batch?: string;
      next_dose_date?: string;
      milking_shift?: string;
      milking_time?: string;
      items: { animal_id: number; weight_kg?: number; production_liters?: number }[];
    }) =>
      apiFetch<{ registered_count: number; message: string }>('/events/batch', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['animals'] });
      qc.invalidateQueries({ queryKey: ['reproduction'] });
      qc.invalidateQueries({ queryKey: ['dairy'] });
    },
  });
}

/** @deprecated use useRegisterBatchEvent */
export function useRegisterBatchWeighing() {
  return useRegisterBatchEvent();
}

export function useRegisterMovement(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch(`/movements/${type}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['movements'] });
      qc.invalidateQueries({ queryKey: ['animals'] });
      qc.invalidateQueries({ queryKey: ['lots'] });
    },
  });
}

export function useBulkImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { property_id: number; animals: Record<string, unknown>[] }) =>
      apiFetch<{ import: { inserted_count: number }; message: string }>('/animals/bulk-import', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['animals'] }),
  });
}

export function useResolveConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      resolution,
      resolution_notes,
      merged_payload,
    }: {
      id: number;
      resolution: string;
      resolution_notes?: string;
      merged_payload?: Record<string, unknown>;
    }) =>
      apiFetch(`/sync/conflicts/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolution, resolution_notes, merged_payload }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sync'] });
    },
  });
}

export function useMe(options?: Partial<UseQueryOptions<User>>) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => apiFetch<{ user: User }>('/auth/me').then((d) => d.user),
    ...options,
  });
}

export function useSalesLots() {
  return useQuery({
    queryKey: ['sales', 'lots'],
    queryFn: () => apiFetch<{ lots: import('@/types/api').SalesLot[] }>('/sales/lots').then((d) => d.lots),
  });
}

export type SalesReport = {
  period: {
    from: string;
    to: string;
    group_by: 'month' | 'year';
    sale_type: string | null;
  };
  totals: {
    lots_count: number;
    shares_sold: number;
    revenue_pipeline: number;
    revenue_collected: number;
    revenue_delivered: number;
    revenue_outstanding: number;
    leads_total: number;
    leads_converted: number;
    leads_pending: number;
  };
  by_sale_type: Array<{
    sale_type: string;
    shares_sold: number;
    revenue_pipeline: number;
    revenue_collected: number;
  }>;
  by_lot_status: Array<{ status: string; count: number }>;
  series: Array<{
    period: string;
    label: string;
    revenue_collected: number;
    revenue_pipeline: number;
    shares_sold: number;
  }>;
  lots: Array<{
    id: number;
    title: string;
    sale_type: string;
    status: string;
    slaughter_date: string | null;
    shares_count: number;
    shares_sold: number;
    revenue_pipeline: number;
    revenue_collected: number;
    progress_percent: number;
  }>;
};

export function useSalesReport(filters: {
  from?: string;
  to?: string;
  sale_type?: string;
  group_by?: 'month' | 'year';
}) {
  const params = new URLSearchParams();
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.sale_type) params.set('sale_type', filters.sale_type);
  if (filters.group_by) params.set('group_by', filters.group_by);

  return useQuery({
    queryKey: ['sales', 'report', filters],
    queryFn: () =>
      apiFetch<{ report: SalesReport }>(`/sales/report?${params}`).then((d) => d.report),
  });
}

export function useSalesLot(id: number) {
  return useQuery({
    queryKey: ['sales', 'lots', id],
    queryFn: () =>
      apiFetch<{ lot: import('@/types/api').SalesLot }>(`/sales/lots/${id}`).then((d) => d.lot),
    enabled: id > 0,
  });
}

export function useCreateSalesLot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<{ lot: import('@/types/api').SalesLot }>('/sales/lots', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'lots'] }),
  });
}

export function useSalesLotAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'publish' | 'slaughter' | 'deliver' }) =>
      apiFetch<{ lot: import('@/types/api').SalesLot }>(`/sales/lots/${id}/${action}`, {
        method: 'POST',
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['sales', 'lots'] });
      qc.invalidateQueries({ queryKey: ['sales', 'lots', vars.id] });
    },
  });
}

export function useCreateSalesShare(lotId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch(`/sales/lots/${lotId}/shares`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'lots', lotId] }),
  });
}

export function useSalesShareAction(lotId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shareId,
      action,
      body,
    }: {
      shareId: number;
      action:
        | 'assign-buyer'
        | 'mark-signal'
        | 'mark-balance'
        | 'mark-delivered'
        | 'unmark-delivered'
        | 'cut-sheet';
      body?: Record<string, unknown>;
    }) => {
      if (action === 'cut-sheet') {
        return apiFetch(`/sales/shares/${shareId}/cut-sheet`, {
          method: 'PUT',
          body: JSON.stringify(body ?? {}),
        });
      }
      return apiFetch(`/sales/shares/${shareId}/${action}`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales', 'lots', lotId] }),
  });
}

export function useSalesLeads() {
  return useQuery({
    queryKey: ['sales', 'leads'],
    queryFn: () =>
      apiFetch<{ leads: import('@/types/api').SalesLead[]; unread_count: number }>('/sales/leads').then(
        (d) => d,
      ),
  });
}

export function useSalesLeadAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'convert' | 'discard' | 'read' }) =>
      apiFetch(`/sales/leads/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'leads'] });
      qc.invalidateQueries({ queryKey: ['sales', 'lots'] });
    },
  });
}

export function useSalesComplianceProfile() {
  return useQuery({
    queryKey: ['sales', 'compliance'],
    queryFn: () =>
      apiFetch<{ profile: import('@/types/api').SalesComplianceProfile | null }>(
        '/sales/compliance-profile',
      ),
  });
}

export function useResolveCompliance() {
  return useMutation({
    mutationFn: (answers: string[]) =>
      apiFetch<{ outcome: { level: string; title: string; body: string; actions?: string[] } | null; disclaimer: string }>(
        '/sales/compliance-profile/resolve',
        { method: 'POST', body: JSON.stringify({ answers }) },
      ),
  });
}

export function useGenerateSalesDocument(lotId: number) {
  return useMutation({
    mutationFn: ({ type, shareId }: { type: string; shareId?: number }) =>
      apiFetch<{ document: { id: number; content: string; type: string } }>(
        `/sales/lots/${lotId}/documents/${type}`,
        {
          method: 'POST',
          body: JSON.stringify(shareId ? { share_id: shareId } : {}),
        },
      ),
  });
}

export type DairyEntry = {
  event_id: number;
  property_id: number;
  event_date: string;
  cow_id: number;
  internal_number: string;
  animal_name: string | null;
  production_liters: number;
  daily_liters: number | null;
  milking_shift: 'morning' | 'afternoon' | 'night' | string;
  milking_time: string | null;
  lot_id: number | null;
  lot_name: string | null;
  fat_pct: number | null;
  protein_pct: number | null;
  lactose_pct: number | null;
  total_solids_pct: number | null;
  ccs: number | null;
  cbt: number | null;
  is_drying_off: boolean;
  drying_off_date: string | null;
  notes: string | null;
  responsible_name: string | null;
  lactation_number: number | null;
  del: number | null;
};

export type DairySummary = {
  property_id: number;
  production_today: number;
  production_month: number;
  avg_per_cow_day: number;
  by_lot: Array<{ lot_id: number | null; lot_name: string; total_liters: number }>;
  top_cows: Array<{
    cow_id: number;
    internal_number: string;
    name: string | null;
    total_liters: number;
    avg_daily_liters: number;
  }>;
  daily_series: Array<{ date: string; total_liters: number }>;
};

export function useDairySummary(propertyId: number | null) {
  return useQuery({
    queryKey: ['dairy', 'summary', propertyId],
    queryFn: () =>
      apiFetch<DairySummary>(`/dairy/summary?property_id=${propertyId}`),
    enabled: !!propertyId,
  });
}

export function useDairyEntries(filters: {
  property_id?: number;
  from?: string;
  to?: string;
  cow_id?: number;
  lot_id?: number;
  lactation?: number;
  page?: number;
}) {
  const params = new URLSearchParams();
  if (filters.property_id) params.set('property_id', String(filters.property_id));
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.cow_id) params.set('cow_id', String(filters.cow_id));
  if (filters.lot_id) params.set('lot_id', String(filters.lot_id));
  if (filters.lactation) params.set('lactation', String(filters.lactation));
  params.set('page', String(filters.page ?? 1));
  params.set('per_page', '25');

  return useQuery({
    queryKey: ['dairy', 'entries', filters],
    queryFn: () =>
      apiFetch<{ entries: DairyEntry[]; meta: PaginationMeta }>(`/dairy/entries?${params}`),
    enabled: !!filters.property_id,
  });
}

export function useDairyReport(filters: {
  property_id?: number;
  from?: string;
  to?: string;
  cow_id?: number;
  lot_id?: number;
  lactation?: number;
}) {
  const params = new URLSearchParams();
  if (filters.property_id) params.set('property_id', String(filters.property_id));
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.cow_id) params.set('cow_id', String(filters.cow_id));
  if (filters.lot_id) params.set('lot_id', String(filters.lot_id));
  if (filters.lactation) params.set('lactation', String(filters.lactation));

  return useQuery({
    queryKey: ['dairy', 'report', filters],
    queryFn: () =>
      apiFetch<{
        from: string;
        to: string;
        total_liters: number;
        daily: Array<{ date: string; total_liters: number; cows_count: number }>;
        entries: DairyEntry[];
      }>(`/dairy/report?${params}`),
    enabled: !!filters.property_id,
  });
}

export function useDairyAnimalHistory(animalId: number | null) {
  return useQuery({
    queryKey: ['dairy', 'animal', animalId],
    queryFn: () =>
      apiFetch<{
        animal_id: number;
        entries: DairyEntry[];
        lactation: { lactation_number: number | null; del: number | null; last_birth_date: string | null };
      }>(`/dairy/animals/${animalId}/history`),
    enabled: !!animalId,
  });
}

export function useDairyPreviousDay(propertyId: number | null, date?: string) {
  return useMutation({
    mutationFn: () => {
      const params = new URLSearchParams();
      if (propertyId) params.set('property_id', String(propertyId));
      if (date) params.set('date', date);
      return apiFetch<{ source_date: string; target_date: string; items: DairyEntry[] }>(
        `/dairy/previous-day?${params}`,
      );
    },
  });
}

export function useDairyLastEntry(propertyId: number | null) {
  return useMutation({
    mutationFn: (cowId?: number) => {
      const params = new URLSearchParams();
      if (propertyId) params.set('property_id', String(propertyId));
      if (cowId) params.set('cow_id', String(cowId));
      return apiFetch<{ entry: DairyEntry | null }>(`/dairy/last-entry?${params}`);
    },
  });
}

export function useRegisterMilkWeighing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch('/events/milk-weighing', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dairy'] });
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['animals'] });
    },
  });
}

export type IndicatorsSummary = {
  property_id: number;
  period: { from: string; to: string };
  growth: {
    animals_with_gmd: number;
    avg_gmd_kg_per_day: number | null;
    median_gmd_kg_per_day: number | null;
  };
  reproduction: {
    covers_total: number;
    covers_pregnant: number;
    pregnancy_rate_by_cover: number | null;
    diagnoses_total: number;
    diagnoses_positive: number;
    pregnancy_rate_by_diagnosis: number | null;
    avg_iep_days: number | null;
    median_iep_days: number | null;
    cows_with_iep_sample: number;
  };
  weaning: {
    weanings_count: number;
    avg_weaning_weight_kg: number | null;
  };
  top_gmd_animals: Array<{
    animal_id: number;
    internal_number: string;
    name: string | null;
    gmd_kg_per_day: number;
  }>;
};

export type IndicatorsAnimalDetail = {
  animal_id: number;
  internal_number: string;
  name: string | null;
  sex: string;
  period: { from: string; to: string };
  current_weight_kg: number | null;
  last_weighing_date: string | null;
  weaning_weight_kg: number | null;
  weaning_date: string | null;
  weight_points: Array<{ date: string; weight_kg: number; source: string }>;
  gmd_intervals: Array<{
    start_date: string;
    end_date: string;
    days: number;
    weight_gain_kg: number;
    gmd_kg_per_day: number;
  }>;
  gmd_avg_kg_per_day: number | null;
  gmd_lifetime_kg_per_day: number | null;
  reproduction?: {
    births_total: number;
    iep_intervals_days: number[];
    avg_iep_days: number | null;
    covers_in_period: number;
  };
  lactation?: Record<string, unknown>;
};

export function useIndicatorsSummary(
  propertyId: number | null,
  from?: string,
  to?: string,
) {
  const params = new URLSearchParams();
  if (propertyId) params.set('property_id', String(propertyId));
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();

  return useQuery({
    queryKey: ['indicators', 'summary', propertyId, from, to],
    queryFn: () => apiFetch<IndicatorsSummary>(`/indicators/summary${qs ? `?${qs}` : ''}`),
    enabled: !!getToken() && !!propertyId,
  });
}

export function useIndicatorsAnimal(animalId: number, from?: string, to?: string) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();

  return useQuery({
    queryKey: ['indicators', 'animal', animalId, from, to],
    queryFn: () =>
      apiFetch<IndicatorsAnimalDetail>(`/indicators/animals/${animalId}${qs ? `?${qs}` : ''}`),
    enabled: !!getToken() && !!animalId,
  });
}

